import { useEffect, useMemo } from 'react'

import type { ChartData, Point } from 'chart.js'
import { format } from 'date-fns/format'
import { useUnit } from 'effector-react'
import groupBy from 'lodash/groupBy'
import { useInfiniteQuery } from 'react-query'

import { LineChart } from '@/components/charts/line-chart'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { $creatorId } from '@/models/creators.ts'
import { listsService } from '@/modules/chatting/service/lists'
import type { ListUser } from '@/modules/chatting/types'
import { handleError } from '@/utils/error'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

interface Props {
  userId: ListUser['id']
}

export const SpendingsHistory = ({ userId }: Props) => {
  const creatorId = useUnit($creatorId)
  const {
    data,
    isLoading: isLoadingBase,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    refetch,
  } = useInfiniteQuery(
    ['spendingsHistory', creatorId, userId],
    ({ pageParam: offset = 0 }) =>
      listsService.getSubscribersSpendingHistory({
        creatorId: creatorId!,
        userId,
        offset,
      }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.hasMore) {
          const allItemsCount = allPages.flatMap((page) => page.list).length

          return allItemsCount
        }
      },
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while getting spending history',
        )
      },
    },
  )

  const isLoading = isLoadingBase || hasNextPage || isFetchingNextPage

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const chartData: ChartData<'line', (number | Point | null)[], unknown> =
    useMemo(() => {
      const allItems =
        data?.pages.flatMap((page) => page.list).filter(isTruthyAndNotEmpty) ||
        []

      const groupedItems = groupBy(allItems, (item) =>
        format(new Date(item.subscribeDate), 'MMM, yyyy'),
      )
      const labels = Object.keys(groupedItems)
      const datasets = [
        {
          data: Object.values(groupedItems).map((items) => {
            return items.reduce((sum, item) => sum + item.price, 0)
          }),
          borderColor: '#1E84E3',
        },
      ]

      return {
        labels,
        datasets,
      }
    }, [data])

  if (isLoading) {
    return <Skeleton className="h-[140px] w-full" />
  }

  if (isError) {
    return (
      <div>
        <p className="text-center">
          Something went wrong while loading spending history
        </p>
        <Button
          className="mt-2 mx-auto block"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </div>
    )
  }

  return (
    <LineChart
      data={chartData}
      options={{
        scales: {
          y: {
            position: 'right',
            ticks: {
              callback: (value) => `$${value}`,
              crossAlign: 'near',
            },
          },
        },
      }}
    />
  )
}
